import { FC, useEffect, useMemo } from 'react';
import store from 'store2';
import { useParams } from 'react-router-dom';
import { images } from '../../assets/img';
import { useRecordInteraction } from '../../context/Appointment/appointment';
import { useAdditionalDealers, useKioskState, usePossibleDealers, useSwitchDealer } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { GenericClickable } from '../../controls/GenericClickable/GenericClickable';
import { Heading } from '../../controls/Heading/Heading';
import { ImageHelper } from '../../controls/ImageHelper/ImageHelper';
import { Spacer } from '../../controls/Spacer/Spacer';
import { Spinner } from '../../controls/Spinner/Spinner';
import { Page } from '../Layout/Page/Page';
import { ScrollableBody } from '../Layout/ScrollableBody/ScrollableBody';
import css from './DealerLanding.module.scss';
import { GMTitle } from '../../controls/Title';
import { Dealer } from '../../context/Kiosk/kiosk.types';

export interface DealerLandingProps {
    onProceed: (returnPage: string) => void;
    onSkip: (returnPage: string) => void;
}

export const DealerLanding: FC<DealerLandingProps> = ({ onProceed, onSkip }) => {
    const Localization = useLocalization();
    const { returnPage } = useParams<{ returnPage: string; }>();
    const {
        dealer: { key: dealerKey },
        kioskType,
    } = useKioskState();
    const possibleDealers = usePossibleDealers();
    const dealers = useAdditionalDealers();
    const switchDealer = useSwitchDealer();
    const recordInteraction: VoidFunction = useRecordInteraction();
    /*
     * Mobile kiosks never present the additional-dealer selection — a mobile-only setup is
     * tied to a single dealer, so skip straight through regardless of the dealer count.
     */
    const skipping = useMemo<boolean>(
        () => kioskType === 'mobile' || dealers.filter((d) => !possibleDealers || possibleDealers.includes(d.key)).length < 2,
        [kioskType, dealers, possibleDealers],
    );

    useEffect(() => {
        recordInteraction();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (skipping) {
            onSkip(returnPage ?? '');
        }
    });

    const onDealerSelected = (dealer: Dealer) => {
        dealer.key !== dealerKey && switchDealer(dealer);
        // assign the dealer key to the DMS appointment waiting in storage (when we have multiple dealers with the same DMS config and came here to disambiguate)
        if (possibleDealers && store.session.has('tempAppointment')) {
            store.session.set('tempAppointment', { ...store.session.get('tempAppointment'),
                dealerKey: dealer.key });
        }
        onProceed(returnPage ?? '');
    };

    const makeToImage = (make: string) => {
        const snaked = make.toLocaleLowerCase()
            .replace(' ', '_');

        return snaked && images['vehicleOEMs'][snaked as keyof typeof images['vehicleOEMs']];
    };

    if (dealers.length === 0) {
        return <Spinner />;
    }

    return (
        <Page>
            {skipping ?
                <Spinner /> :
                <ScrollableBody
                    heading={
                        <Heading
                            primaryHeading={Localization.DealerSelection}
                            secondaryHeading={Localization.PleaseSelectTheDealershipWhereYouAreServicingYourVehicle}
                            showMustache
                        />
                    }
                >
                    {dealers
                        .filter((d) => !possibleDealers || possibleDealers.includes(d.key))
                        .map((dealer, index) => (
                            <div key={dealer.key}>
                                <GenericClickable onClick={() => onDealerSelected(dealer)} title={dealer.key}>
                                    <div className={css.gridRow}>
                                        <div className={css.gridCell}>
                                            <div className={css.image}>
                                                {dealer.make !== null &&
                                                    dealer.make.split('/')
                                                        .map((make) => {
                                                            const image = make && makeToImage(make);

                                                            return (
                                                                image && (
                                                                    <ImageHelper
                                                                        key={dealer.key + make}
                                                                        imageProps={{
                                                                            src: image,
                                                                            alt: Localization.formatString(Localization.Logo, make) as string,
                                                                        }}
                                                                    />
                                                                )
                                                            );
                                                        })}
                                            </div>
                                        </div>
                                        <div className={css.gridCell}>
                                            <GMTitle headerLevel={4}>{dealer.name}</GMTitle>
                                        </div>
                                    </div>
                                </GenericClickable>
                                {index + 1 < dealers.filter((d) => !possibleDealers || possibleDealers.includes(d.key)).length && <Spacer />}
                            </div>
                        ))}
                </ScrollableBody>
            }
        </Page>
    );
};
